// src/components/CalendarWidget.tsx
import React, { useState } from 'react';
import dayjs from 'dayjs';
import DatePicker from '@cpg-its/ads.form.date-picker';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton } from '@mui/material';
import Button from '@cpg-its/ads.common.button';
import { BaseCalendarSettings } from './models';

const toLocalDate = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const CalendarWidget: React.FC<BaseCalendarSettings> = ({
  events,
  featuredEvent,
  filterName,
  initialDate = dayjs().format('YYYY-MM-DD'),
  maxItems = 5,
  calendarPageUrl,
}) => {
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [navPressed, setNavPressed] = useState<'left' | 'right' | null>(null);
  const shiftDate = (delta: number) => {
    const iso = dayjs(selectedDate)
      .add(delta, 'day')
      .format('YYYY-MM-DD');
    setSelectedDate(iso);
  };
  const handleNav = (direction: 'left' | 'right') => {
    setNavPressed(direction);
    shiftDate(direction === 'left' ? -1 : 1);
    window.setTimeout(() => setNavPressed(null), 150);
  };

  const handleDateChange = (_field: string, raw: string) => {
    const iso = dayjs(raw, 'MM/DD/YYYY').format('YYYY-MM-DD');
    setSelectedDate(iso);
  };

  const formatDateRange = (start: string, end?: string) => {
    const startDate = toLocalDate(start);
    const endDate   = end ? toLocalDate(end) : startDate;
    const optsWithYear = {
      month: 'short',
      day:   'numeric',
      year:  'numeric',
    } as const;
    const optsNoYear = {
      month: 'short',
      day:   'numeric',
    } as const;

    // Multi-day: Jan 20 – Jan 22, 2025
    if (endDate.getTime() !== startDate.getTime()) {
      const a = startDate.toLocaleDateString(undefined, optsNoYear);
      const b = endDate.toLocaleDateString(undefined, optsWithYear);
      return `${a} – ${b}`;
    }

    // Single-day: Jan 20, 2025
    return startDate.toLocaleDateString(undefined, optsWithYear);
  };

  const pickStart = toLocalDate(selectedDate).getTime();

  const upcoming = events
  .filter(ev => {
    const startMs = toLocalDate(ev.startDate).getTime();
    const endMs   = ev.endDate
      ? toLocalDate(ev.endDate).getTime()
      : startMs;
    return endMs >= pickStart;
  })
  .sort((a, b) => {
    const dayA = toLocalDate(a.startDate).getTime();
    const dayB = toLocalDate(b.startDate).getTime();
    if (dayA !== dayB) {
      return dayA - dayB;
    }
    // same day → hide time items shown last
    if (a.hideTime !== b.hideTime) {
      return a.hideTime ? 1 : -1;
    }
    return 0;
  })
  .slice(0, maxItems);

  const grouped = upcoming.reduce<Record<string, typeof upcoming>>(
    (acc, ev) => {
      // ← new grouping key uses formatDateRange for single- or multi-day
      const label = formatDateRange(ev.startDate, ev.endDate);
      acc[label] = acc[label] || [];
      acc[label].push(ev);
      return acc;
    },
    {},
  );

  const headingText = filterName
    ? `Upcoming Events  -  ${filterName}`
    : 'Upcoming Events';

  return (
    <div className="calendar-widget">
      <div className="calendar-widget-container">
        <div className="calendar-widget-featured">
          <h2>Featured Event</h2>
          {featuredEvent && (
            <>
              {featuredEvent.thumbnail && (
                <a href= {featuredEvent.url} target="_blank" rel="noopener noreferrer"><img
                  className="calendar-widget-featured-img"
                  src={featuredEvent.thumbnail}
                  alt={featuredEvent.title}
                /></a>
              )}
              <h3 className="calendar-widget-featured-title">
                <a href={featuredEvent.url} target="_blank" rel="noopener noreferrer">{featuredEvent.title}</a>
              </h3>
              {featuredEvent.subtitle && (
                <h4 className="calendar-widget-featured-sub">
                  <a href={featuredEvent.url} target="_blank" rel="noopener noreferrer" >{featuredEvent.subtitle}</a>
                </h4>
              )}
              <p className="calendar-widget-featured-date">
              {formatDateRange(featuredEvent.startDate, featuredEvent.endDate)}
              </p>
              {!featuredEvent.hideTime && (
                <h5 className="calendar-widget-featured-time">
                  {featuredEvent.startTime} – {featuredEvent.endTime} {featuredEvent.timezone}
                </h5>
              )}
              <div
                className="calendar-widget-featured-desc rich-text"
                dangerouslySetInnerHTML={{ __html: featuredEvent.summary }}
              />
            </>
          )}
        </div>

        <div className="calendar-widget-upcoming">
          <h3 className="calendar-widget-upcoming-heading">{headingText}</h3>
          <div className="calendar-widget-upcoming-divider" />

          <div className="calendar-widget-upcoming-datepicker">
            <IconButton
              className="calendar-widget-nav-btn"
              onClick={() => handleNav('left')}
              size="medium"
              aria-pressed={navPressed === 'left'}
              data-pressed={navPressed === 'left'}
            >
              <ChevronLeftIcon htmlColor="black" />
            </IconButton>

            <DatePicker
              field={{
                name: 'dateField',
                value: dayjs(selectedDate).format('MM/DD/YYYY'),
              }}
              form={{ setFieldValue: handleDateChange }}
              noSubText
            />

            <IconButton
              className="calendar-widget-nav-btn"
              onClick={() => handleNav('right')}
              size="medium"
              aria-pressed={navPressed === 'right'}
              data-pressed={navPressed === 'right'}
            >
              <ChevronRightIcon htmlColor="black" />
            </IconButton>

            <div className="calendar-widget-view-all">
              <Button
                onClick={() => window.open(calendarPageUrl, 
                              '_blank', 'noopener,noreferrer')}
                text="View All Events"
                backgroundColor="#006FD4"
              />
            </div>
          </div>

          <div className="calendar-widget-upcoming-divider" />

          <div className="calendar-widget-upcoming-list-container">
          <ul className="calendar-widget-list">
              {Object.values(grouped).length > 0 ? (
                Object.values(grouped).map(evs => {
                  const label = formatDateRange(evs[0].startDate, evs[0].endDate);
                  return (
                    <li key={label} className="calendar-widget-list-item">
                      <h3 className="calendar-widget-list-date">{label}</h3>
                      <div className="calendar-widget-list-events">
                        {evs.map(ev => (
                          <div key={ev.id} className="calendar-widget-list-event">
                            <h4 className="calendar-widget-list-title">
                              <a target="_blank" rel="noopener noreferrer" href={ev.url}>{ev.title}</a>
                            </h4>
                            {!ev.hideTime && (
                              <h5 className="calendar-widget-list-time">
                                {ev.start} – {ev.end} {ev.timezone}
                              </h5>
                            )}
                          </div>
                        ))}
                      </div>
                    </li>
                  );
                })
              ) : (
                <div className="calendar-widget-no-events">No events found.</div>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarWidget;
